/**
 * Utility functions for extracting partial data from incomplete JSON responses
 */

/**
 * Функция для дополнения неполного JSON до объекта с аналитической запиской и визуализациями.
 * Если исходный JSON невалиден, производится ручное извлечение данных.
 */
export const completePartialJson = (text: string): string => {
  console.log('[PARTIAL_DATA] Начало обработки неполного JSON');

  // Если вход пустой, возвращаем базовую структуру
  if (!text || text.trim() === '') {
    return JSON.stringify({ analytical_note: "", visualizations: [] });
  }

  // Если JSON уже валиден – сразу возвращаем его
  try {
    JSON.parse(text);
    console.log('[PARTIAL_DATA] JSON корректен, возвращаем исходный текст');
    return text;
  } catch (e) {
    console.log('[PARTIAL_DATA] JSON не полный, начинаем обработку');
  }

  // Результирующий объект с базовой структурой
  const result: {
    analytical_note: string;
    visualizations: any[];
  } = {
    analytical_note: "",
    visualizations: []
  };

  // Извлекаем аналитическую записку
  const noteMatch = text.match(/"analytical_note"\s*:\s*"((?:\\"|[^"])*?)"/);
  if (noteMatch) {
    let note = noteMatch[1];
    // Если строка заканчивается одиночной обратной косой чертой, удаляем её
    if (note.endsWith('\\') && !note.endsWith('\\\\')) {
      note = note.slice(0, -1);
    }
    result.analytical_note = note
      .replace(/\\"/g, '"')
      .replace(/\\n/g, '')
      .replace(/\\\\/g, '\\');
    console.log('[PARTIAL_DATA] Извлечена аналитическая записка');
  }

  // Извлекаем содержимое массива визуализаций
  const vizArrayMatch = text.match(/"visualizations"\s*:\s*\[([\s\S]*)/);
  if (vizArrayMatch) {
    let vizContent = vizArrayMatch[1].trim();
    // Если отсутствует закрывающая квадратная скобка – добавляем её
    if (!vizContent.endsWith(']')) {
      vizContent += ']';
      console.log('[PARTIAL_DATA] Добавлена отсутствующая закрывающая скобка для visualizations');
    }

    // Сканируем содержимое массива визуализаций вручную
    let index = 0;
    let vizCount = 0;
    while (index < vizContent.length) {
      // Находим начало следующего объекта (символ "{")
      const start = vizContent.indexOf('{', index);
      if (start === -1) break;

      // Сканируем с начала найденного объекта, пытаясь найти соответствующую закрывающую скобку
      let stack: string[] = [];
      let inString = false;
      let escapeNext = false;
      let end = -1;
      for (let i = start; i < vizContent.length; i++) {
        const char = vizContent[i];
        if (escapeNext) {
          escapeNext = false;
          continue;
        }
        if (char === '\\') {
          escapeNext = true;
          continue;
        }
        if (char === '"') {
          inString = !inString;
          continue;
        }
        if (!inString) {
          if (char === '{') {
            stack.push('{');
          } else if (char === '}') {
            stack.pop();
            if (stack.length === 0) {
              end = i;
              break;
            }
          }
        }
      }
      // Если не нашли закрывающую скобку – берем остаток строки как неполный объект
      if (end === -1) {
        end = vizContent.length;
      }
      const objStr = vizContent.substring(start, end + 1);
      vizCount++;

      try {
        // Пытаемся разобрать объект визуализации
        const vizObj = JSON.parse(objStr);
        result.visualizations.push(vizObj);
        console.log(`[PARTIAL_DATA] Визуализация ${vizCount}: успешно разобрана как полный объект`);
      } catch (e) {
        console.log(`[PARTIAL_DATA] Визуализация ${vizCount}: не удалось разобрать объект, извлекаем частично`);
        // Если объект невалиден, пытаемся извлечь отдельные поля
        const partialViz: {
          description: string;
          table_data: {
            headers: string[];
            rows: any[];
          };
          chart_html?: string;
        } = {
          description: "",
          table_data: { headers: [], rows: [] }
        };

        // Извлекаем описание
        const descMatch = objStr.match(/"description"\s*:\s*"((?:\\"|[^"])*?)"/);
        if (descMatch) {
          partialViz.description = descMatch[1].replace(/\\"/g, '"');
        } else {
          partialViz.description = `Визуализация ${vizCount} (неполная)`;
        }

        // Извлекаем заголовки таблицы
        const headersMatch = objStr.match(/"headers"\s*:\s*\[([^\]]*)\]/);
        if (headersMatch) {
          const headers = headersMatch[1].match(/"([^"]+)"/g);
          if (headers) {
            partialViz.table_data.headers = headers.map((h: string) => h.replace(/"/g, ''));
          }
        }

        // Пытаемся извлечь данные строк
        const rowsMatch = objStr.match(/"rows"\s*:\s*\[([\s\S]*)/);
        if (rowsMatch) {
          let rowsContent = rowsMatch[1];
          let rowObjects: any[] = [];
          let idx = 0;
          while (idx < rowsContent.length) {
            let rowStart = rowsContent.indexOf('{', idx);
            if (rowStart === -1) break;
            let rowStack: string[] = [];
            let inRowString = false;
            let rowEscapeNext = false;
            let rowEnd = -1;
            for (let i = rowStart; i < rowsContent.length; i++) {
              const ch = rowsContent[i];
              if (rowEscapeNext) {
                rowEscapeNext = false;
                continue;
              }
              if (ch === '\\') {
                rowEscapeNext = true;
                continue;
              }
              if (ch === '"') {
                inRowString = !inRowString;
                continue;
              }
              if (!inRowString) {
                if (ch === '{') {
                  rowStack.push('{');
                } else if (ch === '}') {
                  rowStack.pop();
                  if (rowStack.length === 0) {
                    rowEnd = i;
                    break;
                  }
                }
              }
            }
            if (rowEnd !== -1) {
              let rowStr = rowsContent.substring(rowStart, rowEnd + 1);
              try {
                let rowObj = JSON.parse(rowStr);
                rowObjects.push(rowObj);
              } catch (e) {
                console.log(`[PARTIAL_DATA] Не удалось разобрать строку объекта: ${(e as Error).message}`);
              }
              idx = rowEnd + 1;
            } else {
              break;
            }
          }
          if (rowObjects.length > 0) {
            partialViz.table_data.rows = rowObjects;
            console.log(`[PARTIAL_DATA] Извлечено ${rowObjects.length} строк(и) для визуализации ${vizCount}`);
          } else {
            partialViz.table_data.rows = [];
            console.log(`[PARTIAL_DATA] Не удалось извлечь строки для визуализации ${vizCount}`);
          }
        }

        // Извлекаем HTML графика, если он есть
        const chartHtmlMatch = objStr.match(/"chart_html"\s*:\s*"((?:\\"|[^"])*?)"/);
        if (chartHtmlMatch) {
          partialViz.chart_html = chartHtmlMatch[1].replace(/\\"/g, '"');
        }

        result.visualizations.push(partialViz);
        console.log(`[PARTIAL_DATA] Визуализация ${vizCount}: извлечены частичные данные`);
      }

      index = end + 1;
    }
    console.log(`[PARTIAL_DATA] Всего извлечено визуализаций: ${vizCount}`);
  } else {
    console.log('[PARTIAL_DATA] Массив visualizations не найден');
  }

  const completedJson = JSON.stringify(result, null, 2);
  console.log('[PARTIAL_DATA] Обработка завершена, сформирован завершённый JSON');
  return completedJson;
};

/**
 * Функция для извлечения данных из (возможно) частичного ответа.
 * Поддерживает как новый блочный формат, так и старый формат (analytical_note + visualizations).
 */
export const extractPartialData = (text: string): any => {
  // First try to parse as complete JSON
  try {
    const parsed = JSON.parse(text);
    console.log('[PARTIAL_DATA] JSON корректен, возвращаем разобранный объект');
    return parsed;
  } catch (e) {
    console.log('[PARTIAL_DATA] JSON не полный, начинаем частичное извлечение');
  }

  // Check if it's the new blocks format
  if (text.includes('"blocks"')) {
    console.log('[PARTIAL_DATA] Обнаружен новый блочный формат');
    const blocks = extractBlocks(text);
    if (blocks.length > 0) {
      return { blocks };
    }
  }

  // Fall back to old format extraction
  console.log('[PARTIAL_DATA] Используем старый формат извлечения');
  const completedJson = completePartialJson(text);
  try {
    const parsed = JSON.parse(completedJson);
    console.log('[PARTIAL_DATA] Данные успешно разобраны из завершённого JSON');
    return parsed;
  } catch (e) {
    console.error('[PARTIAL_DATA] Ошибка разбора завершённого JSON:', (e as Error).message);
    return {};
  }
};

/**
 * Extract report title from partial JSON text.
 * Used by PartialSummaryDisplay component.
 */
export const extractReportTitle = (text: string): string => {
  const match = text.match(/"report_title"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (match) {
    return match[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  return '';
};

/**
 * Extract summary text from partial JSON.
 * Used by PartialSummaryDisplay component.
 */
export const extractSummary = (text: string): string => {
  const match = text.match(/"summary"\s*:\s*"((?:[^"\\]|\\.)*)"/);
  if (match) {
    return match[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
  }
  return '';
};

/**
 * Extract key findings array from partial JSON.
 * Returns array of findings even if JSON is incomplete.
 */
export const extractKeyFindings = (text: string): string[] => {
  const findings: string[] = [];

  // Try to find key_findings array
  const arrayMatch = text.match(/"key_findings"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (!arrayMatch) return findings;

  const arrayContent = arrayMatch[1];

  // Extract individual strings from array using exec loop
  const regex = /"((?:[^"\\]|\\.)*)"/g;
  let match;
  while ((match = regex.exec(arrayContent)) !== null) {
    const finding = match[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
    if (finding.trim()) {
      findings.push(finding);
    }
  }

  return findings;
};

/**
 * Extract recommendations array from partial JSON.
 * Returns array of recommendations even if JSON is incomplete.
 */
export const extractRecommendations = (text: string): string[] => {
  const recommendations: string[] = [];

  // Try to find recommendations array
  const arrayMatch = text.match(/"recommendations"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (!arrayMatch) return recommendations;

  const arrayContent = arrayMatch[1];

  // Extract individual strings from array using exec loop
  const regex = /"((?:[^"\\]|\\.)*)"/g;
  let match;
  while ((match = regex.exec(arrayContent)) !== null) {
    const recommendation = match[1].replace(/\\"/g, '"').replace(/\\n/g, '\n');
    if (recommendation.trim()) {
      recommendations.push(recommendation);
    }
  }

  return recommendations;
};

/**
 * Extract blocks array from partial JSON (new block-based format).
 * Returns array of complete blocks even if JSON is incomplete.
 */
export const extractBlocks = (text: string): any[] => {
  const blocks: any[] = [];

  // Try to find blocks array
  const arrayMatch = text.match(/"blocks"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (!arrayMatch) return blocks;

  const arrayContent = arrayMatch[1];

  // Extract individual block objects
  let index = 0;
  while (index < arrayContent.length) {
    const start = arrayContent.indexOf('{', index);
    if (start === -1) break;

    // Find matching closing brace
    let stack: string[] = ['{'];
    let inString = false;
    let escape = false;
    let end = -1;

    for (let i = start + 1; i < arrayContent.length; i++) {
      const char = arrayContent[i];

      if (escape) {
        escape = false;
        continue;
      }

      if (char === '\\') {
        escape = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (char === '{') {
          stack.push('{');
        } else if (char === '}') {
          stack.pop();
          if (stack.length === 0) {
            end = i;
            break;
          }
        }
      }
    }

    if (end !== -1) {
      const objStr = arrayContent.substring(start, end + 1);
      try {
        const block = JSON.parse(objStr);

        // Validate block structure
        if (block.type) {
          const isValid =
            (block.type === 'heading' && block.text) ||
            (block.type === 'text' && block.content) ||
            (block.type === 'visualization' && block.description);

          if (isValid) {
            blocks.push(block);
            console.log(`[PARTIAL_DATA] Extracted block: type=${block.type}`);
          }
        }
      } catch (e) {
        console.warn('[PARTIAL_DATA] Failed to parse block object:', e);
      }
      index = end + 1;
    } else {
      break;
    }
  }

  console.log(`[PARTIAL_DATA] Total blocks extracted: ${blocks.length}`);
  return blocks;
};

/**
 * Extract reports array from analytics response.
 * Used by AnalyticsStep component.
 */
export const extractReports = (text: string): any[] => {
  const reports: any[] = [];

  // Try to find reports array
  const arrayMatch = text.match(/"reports"\s*:\s*\[([\s\S]*?)(?:\]|$)/);
  if (!arrayMatch) return reports;

  const arrayContent = arrayMatch[1];

  // Extract individual report objects
  let index = 0;
  while (index < arrayContent.length) {
    const start = arrayContent.indexOf('{', index);
    if (start === -1) break;

    // Find matching closing brace
    let stack: string[] = ['{'];
    let inString = false;
    let escape = false;
    let end = -1;

    for (let i = start + 1; i < arrayContent.length; i++) {
      const char = arrayContent[i];

      if (escape) {
        escape = false;
        continue;
      }

      if (char === '\\') {
        escape = true;
        continue;
      }

      if (char === '"') {
        inString = !inString;
        continue;
      }

      if (!inString) {
        if (char === '{') {
          stack.push('{');
        } else if (char === '}') {
          stack.pop();
          if (stack.length === 0) {
            end = i;
            break;
          }
        }
      }
    }

    if (end !== -1) {
      const objStr = arrayContent.substring(start, end + 1);
      try {
        const report = JSON.parse(objStr);
        reports.push(report);
      } catch (e) {
        console.warn('[PARTIAL_DATA] Failed to parse report object:', e);
      }
      index = end + 1;
    } else {
      break;
    }
  }

  return reports;
};
